import { ReactNode, useEffect, useState } from 'react';
import { PLACEMENT, Popover } from 'baseui/popover';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { HeadingSmallBold } from '@tigergraph/app-ui-lib/typography';
import { format } from 'date-fns';
import { CopyIcon, X } from 'lucide-react';
import useCopyClipboard from 'react-use-clipboard';

import { popOverrides } from '@/components/popOverride';
import IconButton from '@/components/IconButton';
import { showToast } from '@/components/styledToasterContainer';
import { parseDate } from '@/lib/date';
import { DatabaseIcon } from '@/pages/home/icons';
import { InfoContainer, InfoContent, InfoTitle } from '@/pages/workgroup/tab/styled';
import { DatabaseT } from '@/pages/workgroup/type';

export function DatabasePopoverContent({ onClose, database }: { database: DatabaseT; onClose: () => void }) {
  const [css] = useStyletron();
  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        width: 'min(80vw,500px)',
      })}
      id={`popover-${database.database_id}`}
    >
      <DatabaseHeader onClose={onClose} database={database} />
      <DatabaseInfo database={database} />
    </div>
  );
}

function DatabaseHeader({ database, onClose }: { database: DatabaseT; onClose: () => void }) {
  const [css] = useStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })}
    >
      <DatabaseIcon />
      <HeadingSmallBold $style={{ fontSize: '18px', lineHeight: '32px' }}>{database.name}</HeadingSmallBold>
      <span
        className={css({
          flex: 1,
        })}
      />
      <IconButton onClick={onClose}>
        <X size={24} />
      </IconButton>
    </div>
  );
}

export function DatabaseInfo({ database }: { database: DatabaseT }) {
  const [workgroupIdCopied, setWorkgroupIdCopied] = useCopyClipboard(database.workgroup_id, {
    successDuration: 1000,
  });

  const [databaseIdCopied, setDatabaseIdCopied] = useCopyClipboard(database.database_id, {
    successDuration: 1000,
  });

  useEffect(() => {
    if (workgroupIdCopied) {
      showToast({
        kind: 'positive',
        message: 'Workgroup ID copied successfully.',
      });
    }
  }, [workgroupIdCopied]);

  useEffect(() => {
    if (databaseIdCopied) {
      showToast({
        kind: 'positive',
        message: 'Database ID copied successfully.',
      });
    }
  }, [databaseIdCopied]);

  return (
    <InfoContainer>
      <InfoTitle>ID:</InfoTitle>
      <InfoContent>
        <span>{database.database_id}</span>
        <IconButton type="button" onClick={setDatabaseIdCopied}>
          <CopyIcon size="12" />
        </IconButton>
      </InfoContent>
      <InfoTitle>Workgroup ID:</InfoTitle>
      <InfoContent>
        <span>{database.workgroup_id}</span>
        <IconButton type="button" onClick={setWorkgroupIdCopied}>
          <CopyIcon size="12" />
        </IconButton>
      </InfoContent>
      <InfoTitle>Create Time:</InfoTitle>
      <InfoContent>{format(parseDate(database.create_time), 'yyyy-MM-dd HH:mm:ss')}</InfoContent>
    </InfoContainer>
  );
}

export function DatabaseNamePopover({
  database,
  children,
  className,
}: {
  database: DatabaseT;
  children: ReactNode;
  className?: string;
}) {
  const [showPopover, setShowPopover] = useState(false);

  const delayClose = () => {
    setTimeout(() => {
      setShowPopover(false);
    }, 100);
  };

  return (
    <Popover
      isOpen={showPopover}
      onEsc={delayClose}
      onClickOutside={() => {}}
      onClick={(e) => {
        e.stopPropagation();
        setShowPopover((prev) => !prev);
      }}
      content={<DatabasePopoverContent database={database} onClose={() => setShowPopover(false)} />}
      placement={PLACEMENT.bottom}
      overrides={popOverrides}
      autoFocus={false}
    >
      <span className={className} style={{ cursor: 'pointer' }} title={database.name}>
        {children}
      </span>
    </Popover>
  );
}
